import { useEffect, useState } from "react";
import { WITH_COOKIE } from "../tokenStorage";
import { AdminNav } from "./AdminPage";

interface UploadedMedia {
  url: string;
  filename: string;
  format: string;
  byte_size: number;
  created_at: string;
}

interface AdminMediaPageProps {
  apiUrl: string;
  token: string;
  tokenResolved: boolean;
}

const IMAGE_FORMATS = ["png", "jpg", "gif", "webp", "svg"];
const STUDIO_FORMATS = ["embdoc", "pes", "dst", "exp", "jef", "vp3"];

async function failure(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body.detail === "string") return body.detail;
  } catch {
    // Not JSON: fall through to the status.
  }
  return `HTTP ${res.status}`;
}

function size(bytes: number): string {
  return bytes < 1_048_576 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / 1_048_576).toFixed(1)} MB`;
}

/** What to paste into a tutorial's .mdx file to use this upload. */
function snippet({ url, filename, format }: UploadedMedia): string {
  const name = filename.replace(/\.[^.]+$/, "").replaceAll("-", " ");
  if (STUDIO_FORMATS.includes(format)) return `<StudioFile url="${url}" replay="" name="${name}" />`;
  if (IMAGE_FORMATS.includes(format)) return `![${name}](${url})`;
  return `[${filename}](${url})`;
}

/**
 * Files to link to from tutorials (app/routers/admin_media.py), the admin's
 * alone on dev too. A file's URL is the hash of its bytes, so changing one
 * means uploading it again and updating the link. dev and prod keep separate
 * media, and tutorials link prod's, so upload here on prod.
 */
export function AdminMediaPage({ apiUrl, token, tokenResolved }: AdminMediaPageProps) {
  const [access, setAccess] = useState<"checking" | "denied" | "granted">("checking");
  const [media, setMedia] = useState<UploadedMedia[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const load = async () => {
    const res = await fetch(`${apiUrl}/api/admin/media/list`, {
      ...WITH_COOKIE,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token || null }),
    });
    if (!res.ok) return false;
    setMedia((await res.json()).media);
    return true;
  };

  useEffect(() => {
    if (!tokenResolved) return;
    load()
      .then((ok) => setAccess(ok ? "granted" : "denied"))
      .catch((err) => {
        setError(`Could not reach the server: ${err}`);
        setAccess("denied");
      });
  }, [tokenResolved, token]);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        if (token) form.append("token", token);
        const res = await fetch(`${apiUrl}/api/admin/media`, { ...WITH_COOKIE, method: "POST", body: form });
        if (!res.ok) {
          setError(`${file.name}: ${await failure(res)}`);
          break;
        }
      }
      await load();
    } catch (err) {
      setError(`Could not reach the server: ${err}`);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(text);
  };

  if (access === "checking") return <p aria-busy="true">Loading…</p>;

  if (access === "denied") {
    return (
      <article>
        <h2>Admin</h2>
        <p>{error ?? "This page isn't available."}</p>
      </article>
    );
  }

  return (
    <>
      <AdminNav current="media" />
      <h2>Media</h2>
      <p>
        Hosted under <code>/assets/media</code> for tutorials to link to: Stitch Studio designs, machine files,
        images, PDFs and zips, up to 25 MB.
      </p>
      <label>
        Upload
        <input
          type="file"
          multiple
          disabled={busy}
          aria-busy={busy}
          onChange={(e) => {
            void upload(e.target.files);
            e.target.value = "";
          }}
        />
      </label>
      {error && (
        <p role="alert" style={{ color: "var(--pico-del-color, #b3261e)" }}>
          {error}
        </p>
      )}
      {media.length === 0 ? (
        <p>Nothing uploaded yet.</p>
      ) : (
        <div className="overflow-auto">
          <table>
            <thead>
              <tr>
                <th>File</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Use it</th>
              </tr>
            </thead>
            <tbody>
              {media.map((item) => (
                <tr key={item.url}>
                  <td>
                    <a href={item.url}>{item.filename}</a>
                  </td>
                  <td>{size(item.byte_size)}</td>
                  <td>{new Date(item.created_at).toLocaleDateString()}</td>
                  <td>
                    <button type="button" className="outline" onClick={() => copy(snippet(item))}>
                      {copied === snippet(item) ? "Copied" : "Copy for a post"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
